/**
 * TextPropertiesEditor Component
 * 
 * Property editor for Text block type.
 */

import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextBlockProps } from '@/types/menuBuilder';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Palette } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TextPropertiesEditorProps {
  props: TextBlockProps;
  onUpdate: (props: Partial<TextBlockProps>) => void;
}

export const TextPropertiesEditor = memo(function TextPropertiesEditor({
  props,
  onUpdate,
}: TextPropertiesEditorProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedColor, setSelectedColor] = useState('#EF4444'); // Default red

  const insertColor = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = props.content || '';
    const selectedText = text.substring(start, end);
    
    if (!selectedText) return; // Only wrap if text is selected
    
    const newText = text.substring(0, start) + 
      `<span style="color: ${selectedColor}">${selectedText}</span>` + 
      text.substring(end);
      
    onUpdate({ content: newText });
  };

  return (
    <div className="space-y-6">
      {/* Content */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t('menuBuilder.content', 'Content')}</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 gap-1">
                <Palette className="h-3 w-3" />
                <span className="text-xs">Color Text</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3">
              <div className="space-y-2">
                <Label className="text-xs">Select color for selected text</Label>
                <div className="flex gap-2">
                  <Input 
                    type="color" 
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    className="w-10 h-8 p-1"
                  />
                  <Input 
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    className="flex-1 h-8 text-xs"
                  />
                </div>
                <Button size="sm" className="w-full" onClick={insertColor}>
                  Apply Color
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  Select text in the box below, then click Apply.
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <Textarea
          ref={textareaRef}
          value={props.content || ''}
          onChange={(e) => onUpdate({ content: e.target.value })}
          placeholder={t('menuBuilder.textPlaceholder', 'Enter your text here...')}
          rows={5}
        />
        <p className="text-[10px] text-muted-foreground">
          Supports HTML for advanced styling (e.g., &lt;b&gt;bold&lt;/b&gt;, &lt;i&gt;italic&lt;/i&gt;)
        </p>
      </div>

      {/* Arabic Content */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.contentAr', 'Arabic Content (Optional)')}</Label>
        <Textarea
          value={props.contentAr || ''}
          onChange={(e) => onUpdate({ contentAr: e.target.value })}
          placeholder={t('menuBuilder.textPlaceholderAr', 'Enter Arabic text...')}
          rows={3}
          dir="rtl"
        />
      </div>

      {/* Font Size */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.fontSize', 'Font Size')}</Label>
        <div className="flex gap-2">
          <Select
            value={props.fontSize || 'md'}
            onValueChange={(value) =>
              onUpdate({ fontSize: value as TextBlockProps['fontSize'] })
            }
          >
            <SelectTrigger className="flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sm">{t('common.small', 'Small')}</SelectItem>
              <SelectItem value="md">{t('common.medium', 'Medium')}</SelectItem>
              <SelectItem value="lg">{t('common.large', 'Large')}</SelectItem>
              <SelectItem value="xl">{t('common.extraLarge', 'Extra Large')}</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          
          {props.fontSize === 'custom' && (
            <div className="w-24 relative">
              <Input
                type="number"
                value={props.customFontSize || 16}
                onChange={(e) => onUpdate({ customFontSize: Number(e.target.value) })}
                className="pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                px
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Font Weight */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.fontWeight', 'Font Weight')}</Label>
        <Select
          value={props.fontWeight || 'normal'}
          onValueChange={(value) =>
            onUpdate({ fontWeight: value as TextBlockProps['fontWeight'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="normal">{t('menuBuilder.normal', 'Normal')}</SelectItem>
            <SelectItem value="medium">{t('common.medium', 'Medium')}</SelectItem>
            <SelectItem value="semibold">{t('menuBuilder.semibold', 'Semi-Bold')}</SelectItem>
            <SelectItem value="bold">{t('menuBuilder.bold', 'Bold')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Alignment */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.alignment', 'Alignment')}</Label>
        <Select
          value={props.alignment}
          onValueChange={(value) =>
            onUpdate({ alignment: value as TextBlockProps['alignment'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left">{t('common.left', 'Left')}</SelectItem>
            <SelectItem value="center">{t('common.center', 'Center')}</SelectItem>
            <SelectItem value="right">{t('common.right', 'Right')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Padding */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.padding', 'Padding')}</Label>
        <Select
          value={props.padding || 'md'}
          onValueChange={(value) =>
            onUpdate({ padding: value as TextBlockProps['padding'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('common.none', 'None')}</SelectItem>
            <SelectItem value="sm">{t('common.small', 'Small')}</SelectItem>
            <SelectItem value="md">{t('common.medium', 'Medium')}</SelectItem>
            <SelectItem value="lg">{t('common.large', 'Large')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Text Color */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.textColor', 'Text Color')}</Label>
        <div className="flex gap-2">
          <Input
            type="color"
            value={props.textColor || '#000000'}
            onChange={(e) => onUpdate({ textColor: e.target.value })}
            className="w-12 h-10 p-1 cursor-pointer"
          />
          <Input
            value={props.textColor || ''}
            onChange={(e) => onUpdate({ textColor: e.target.value })}
            placeholder="#000000"
            className="flex-1"
          />
        </div>
      </div>

      {/* Background Color */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.backgroundColor', 'Background Color')}</Label>
        <div className="flex gap-2">
          <Input
            type="color"
            value={props.backgroundColor || '#ffffff'}
            onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
            className="w-12 h-10 p-1 cursor-pointer"
          />
          <Input
            value={props.backgroundColor || ''}
            onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
            placeholder="transparent"
            className="flex-1"
          />
        </div>
      </div>
    </div>
  );
});

export default TextPropertiesEditor;
